import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { stepGroupLeaderSchema, type StepGroupLeaderForm } from './schemas';
import { useRegistrationStore } from '../../store/registrationStore';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../Button';

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';
const errorClasses = 'text-xs text-error mt-1';

interface StepGroupLeaderProps {
  onNext: () => void;
}

export function StepGroupLeader({ onNext }: StepGroupLeaderProps) {
  const user = useAuthStore((s) => s.user);
  const { groupLeaderData, setGroupLeaderData } = useRegistrationStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StepGroupLeaderForm>({
    resolver: zodResolver(stepGroupLeaderSchema),
    defaultValues: {
      ...groupLeaderData,
      familyConnection: groupLeaderData.familyConnection || '',
    },
  });

  const onSubmitStep = (data: StepGroupLeaderForm) => {
    setGroupLeaderData(data);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitStep)} className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Group Leader Information</h2>

      {/* Read-only user info from auth */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClasses}>Name</label>
          <p className="p-3 bg-slate-100 rounded-lg text-slate-700">
            {user?.firstName} {user?.lastName}
          </p>
        </div>
        <div>
          <label className={labelClasses}>Email</label>
          <p className="p-3 bg-slate-100 rounded-lg text-slate-700">{user?.email}</p>
        </div>
      </div>

      {/* Editable phone */}
      <div>
        <label htmlFor="gl-phone" className={labelClasses}>
          Phone Number
        </label>
        <input
          id="gl-phone"
          type="tel"
          className={inputClasses}
          placeholder="(555) 123-4567"
          {...register('phone')}
        />
        {errors.phone && <p className={errorClasses}>{errors.phone.message}</p>}
      </div>

      {/* Family Connection */}
      <div>
        <label htmlFor="gl-family-connection" className={labelClasses}>
          How is your family connected to the Goldson-Wade family? <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <p className="text-xs text-slate-400 mb-2">
          This helps us understand how everyone is related and build our family tree.
        </p>
        <textarea
          id="gl-family-connection"
          className={inputClasses}
          rows={3}
          placeholder="e.g., My grandmother was one of six siblings from the original Goldson family in Jamaica."
          {...register('familyConnection')}
        />
      </div>

      {/* Group leader toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isGroupLeader"
          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
          {...register('isGroupLeader')}
        />
        <label htmlFor="isGroupLeader" className="text-sm text-slate-700">
          I am registering as a Group Leader (managing a family unit)
        </label>
      </div>

      <div className="flex justify-end">
        <Button type="submit" variant="primary">
          Next: Family Members
        </Button>
      </div>
    </form>
  );
}
